import { createGlProgram, loseGl } from './glProgram';
import { ILLUS_FS, ILLUS_VS } from './illustratedCardShader';
import { METAL_FS, METAL_VS } from './metalCardShader';
import { MORPH_FS, MORPH_VS } from './morphShader';
import { PATTERN_FS, PATTERN_PRE, PATTERN_VS } from './patternCardShaders';

// Compiles every card shader once in throwaway contexts so the browser's shader
// cache already holds them: the first switch to a design then doesn't hitch
// (card-transitions hand-off). One program per step, spaced out to stay off the
// main thread's critical path. Returns a cancel function.
export const warmCardShaders = (): (() => void) => {
  const jobs: { webgl2: boolean; vs: string; fs: string }[] = [
    { webgl2: false, vs: MORPH_VS, fs: MORPH_FS },
    { webgl2: false, vs: ILLUS_VS, fs: ILLUS_FS },
    { webgl2: false, vs: METAL_VS, fs: METAL_FS },
    ...Object.values(PATTERN_FS).map((fs) => ({
      webgl2: true,
      vs: PATTERN_VS,
      fs: PATTERN_PRE + fs,
    })),
  ];
  const contexts: {
    gl1?: WebGLRenderingContext;
    gl2?: WebGL2RenderingContext;
  } = {};
  const context = (webgl2: boolean) => {
    const c = document.createElement('canvas');
    if (webgl2) return (contexts.gl2 ??= c.getContext('webgl2') ?? undefined);
    return (contexts.gl1 ??= c.getContext('webgl') ?? undefined);
  };

  let timer = 0;
  let i = 0;
  const next = () => {
    const job = jobs[i++];
    if (!job) return dispose();
    const gl = context(job.webgl2);
    // Reading the compile/link status forces the compile now rather than at first draw.
    if (gl) createGlProgram(gl, job.vs, job.fs);
    timer = window.setTimeout(next, 200);
  };
  const dispose = () => {
    window.clearTimeout(timer);
    loseGl(contexts.gl1);
    loseGl(contexts.gl2);
    contexts.gl1 = undefined;
    contexts.gl2 = undefined;
    i = jobs.length;
  };
  next();
  return dispose;
};
